<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AppIcon from "./AppIcon.vue";
import { useSesion } from "@/composables/useSesion";
import { useTheme } from "@/composables/useTheme";

/**
 * El menú de la cuenta: quién eres, cómo se ve Chuy y la puerta de salida.
 *
 * En computadora cuelga del botón; en el teléfono sube como hoja desde abajo,
 * sacada al `body` para que la barra de arriba (que tiene su propio desenfoque)
 * no la recorte.
 */
const { sesion, esAdmin, salir } = useSesion();
const { preference, apply } = useTheme();
const route = useRoute();

const abierto = ref(false);
const contenedor = ref<HTMLElement | null>(null);
const hoja = ref<HTMLElement | null>(null);

const conCuenta = computed(() => Boolean(sesion.value && sesion.value.username !== "local"));
const iniciales = computed(() =>
  (sesion.value?.name ?? "")
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? "")
    .join(""),
);

const TEMAS = [
  { value: "light", icon: "sun", label: "Claro" },
  { value: "dark", icon: "moon", label: "Oscuro" },
  { value: "system", icon: "sparkle", label: "Como la máquina" },
] as const;

const RENGLON =
  "toque flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-left text-[14px] text-secondary transition-colors hover:bg-surface-hover hover:text-content md:py-2 md:text-[13px]";

function afuera(evento: MouseEvent): void {
  const blanco = evento.target as Node;
  if (contenedor.value?.contains(blanco) || hoja.value?.contains(blanco)) return;
  abierto.value = false;
}
function alTeclear(evento: KeyboardEvent): void {
  if (evento.key === "Escape") abierto.value = false;
}

watch(() => route.fullPath, () => (abierto.value = false));
onMounted(() => {
  document.addEventListener("mousedown", afuera);
  document.addEventListener("keydown", alTeclear);
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", afuera);
  document.removeEventListener("keydown", alTeclear);
});
</script>

<template>
  <div ref="contenedor" class="relative">
    <button
      class="toque flex items-center gap-2 rounded-full border border-separator bg-surface py-1 pr-2 pl-1 transition-colors hover:bg-surface-hover"
      :title="conCuenta ? `${sesion?.name} (@${sesion?.username})` : 'Menú'"
      :aria-expanded="abierto"
      aria-haspopup="menu"
      @click="abierto = !abierto"
    >
      <span
        v-if="conCuenta"
        class="grid size-7 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-bold text-accent"
      >
        {{ iniciales }}
      </span>
      <span v-else class="grid size-7 place-items-center text-secondary"><AppIcon name="settings" :size="15" /></span>
      <AppIcon name="chevronRight" :size="13" class="rotate-90 text-tertiary" />
    </button>

    <Transition name="modal">
      <div
        v-if="abierto"
        class="modal-panel card absolute right-0 z-40 mt-1.5 hidden w-[240px] overflow-hidden p-1 md:block"
        role="menu"
      >
        <div v-if="conCuenta" class="border-b border-separator px-3 py-2.5">
            <p class="truncate text-[13px] font-semibold text-content">{{ sesion?.name }}</p>
            <p class="truncate text-[11.5px] text-tertiary">
              @{{ sesion?.username }}<template v-if="esAdmin"> · administrador</template>
            </p>
          </div>
          <div class="px-2 py-2">
            <div class="flex items-center gap-1 rounded-[10px] bg-surface-muted p-[3px]">
              <button
                v-for="tema in TEMAS"
                :key="tema.value"
                class="flex flex-1 items-center justify-center rounded-[8px] py-1.5 text-secondary transition-all"
                :class="preference === tema.value ? 'bg-surface text-content shadow-soft' : ''"
                :aria-label="tema.label"
                :title="tema.label"
                @click="apply(tema.value)"
              >
                <AppIcon :name="tema.icon" :size="14" />
              </button>
            </div>
          </div>
          <div class="border-t border-separator pt-1">
            <RouterLink v-if="conCuenta" to="/mi-contrasena" :class="RENGLON">
              <AppIcon name="key" :size="15" />
              Mi contraseña
            </RouterLink>
            <RouterLink to="/ajustes" :class="RENGLON">
              <AppIcon name="settings" :size="15" />
              Ajustes
            </RouterLink>
            <button v-if="conCuenta" :class="RENGLON" @click="salir()">
              <AppIcon name="logout" :size="15" />
              Salir
            </button>
          </div>
      </div>
    </Transition>
  </div>

  <Teleport to="body">
    <Transition name="modal">
      <div v-if="abierto" class="md:hidden">
        <div class="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px]" @click="abierto = false" />
        <div
          ref="hoja"
          class="modal-panel pb-safe-plus card fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto rounded-b-none px-1.5 pt-1.5"
          role="menu"
        >
          <div class="flex justify-center pt-1.5 pb-1"><span class="h-1 w-9 rounded-full bg-separator-strong" /></div>
          <div v-if="conCuenta" class="flex items-center gap-2.5 border-b border-separator px-3 py-2.5">
            <span class="grid size-9 place-items-center rounded-full bg-accent-soft text-[12px] font-bold text-accent">{{ iniciales }}</span>
            <span class="min-w-0">
              <span class="block truncate text-[14px] font-semibold text-content">{{ sesion?.name }}</span>
              <span class="block truncate text-[12px] text-tertiary">@{{ sesion?.username }}</span>
            </span>
          </div>
          <div class="px-2 py-2.5">
            <p class="px-1 pb-1.5 text-[10.5px] font-semibold tracking-[0.06em] text-tertiary uppercase">Cómo se ve</p>
            <div class="flex items-center gap-1 rounded-[10px] bg-surface-muted p-[3px]">
              <button
                v-for="tema in TEMAS"
                :key="tema.value"
                class="toque flex flex-1 items-center justify-center gap-1.5 rounded-[8px] py-2 text-[12px] text-secondary transition-all"
                :class="preference === tema.value ? 'bg-surface text-content shadow-soft' : ''"
                @click="apply(tema.value)"
              >
                <AppIcon :name="tema.icon" :size="14" />
                {{ tema.label }}
              </button>
            </div>
          </div>
          <div class="border-t border-separator pt-1">
            <RouterLink v-if="conCuenta" to="/mi-contrasena" :class="RENGLON">
              <AppIcon name="key" :size="15" />
              Mi contraseña
            </RouterLink>
            <button v-if="conCuenta" :class="RENGLON" @click="salir()">
              <AppIcon name="logout" :size="15" />
              Salir
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
